import { useAuth0 } from "@auth0/auth0-react";
import { config } from "@config/config";
import { asyncWithLDProvider, basicLogger } from "launchdarkly-react-client-sdk";
import { PropsWithChildren, ReactNode, useEffect, useState } from "react";

import { Headline } from "@fronterahealth/frontera-ui-components/Headline/Headline";

/**
 * https://docs.launchdarkly.com/sdk/client-side/react/react-web
 * This provider prevents loading of child nodes until LaunchDarkly is fully loaded
 * Synchronous loading of the LaunchDarkly SDK means the flags are unavailable until the App is mounted
 * Our app requires flags be available prior to App mount because we use flags for routing
 */
export const FeatureFlagProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { user, isLoading: isUserLoading } = useAuth0();
  const [isLDLoading, setIsLDLoading] = useState(true);

  // React providers must provide a consistent signature (i.e. accept children)
  const dummyProvider = ({ children }: { children: ReactNode }) => <>{children}</>;
  const [LDProvider, setLDProvider] = useState(() => dummyProvider);

  useEffect(() => {
    const initializeLDProvider = async () => {
      if (!user) return;

      const orgName = `${user[config.auth0Audience + "/orgName"] ?? "no-org"}`;
      const rolesKey = `${config.auth0Audience}/roles`;
      const rolesString = user?.[rolesKey];

      const Provider = await asyncWithLDProvider({
        clientSideID: import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID,
        reactOptions: { useCamelCaseFlagKeys: false },
        options: { logger: basicLogger({ level: "warn" }) },
        context: {
          kind: "user",
          key: user.sub,
          name: user.name,
          email: user.email,
          custom: {
            orgName,
            sub: user.sub,
          },
          roles: rolesString.length ? rolesString : "",
        },
      });

      setLDProvider(() => Provider);
      setIsLDLoading(false);
    };

    initializeLDProvider();
  }, [user]);

  if (isUserLoading || isLDLoading || !user || !LDProvider) {
    return (
      <div className="flex h-screen flex-col items-center justify-center">
        <Headline size="medium" className="text-limestone-400">
          Loading...
        </Headline>
      </div>
    );
  }

  return <LDProvider>{children}</LDProvider>;
};
